import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { economiaApi, commesseApi, reportPersonaleApi } from '../services/api'
import { TrendingUp, Euro, Clock, AlertTriangle } from 'lucide-react'

interface Commessa { id: number; numero_commessa: string; nome: string }

interface EcoReport {
  commessa: { id: number; numero: string; nome: string; contract_value: number; status: string }
  costi_lavoro: { total_hours: number; total_cost: number; dettaglio: { tecnico_nome: string; ore: number; costo: number }[] }
  spese: { totale: number; n_voci: number }
  marginalita: { total_costs: number; margin_amount: number; margin_percentage: number; status: 'verde' | 'giallo' | 'rosso' }
}

const MARGIN_CLS = {
  verde: { bar: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  giallo: { bar: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  rosso: { bar: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
}

export default function ReportEconomico() {
  const [selectedCommessa, setSelectedCommessa] = useState<number | null>(null)
  const [anno, setAnno] = useState(new Date().getFullYear())
  const [mese, setMese] = useState(new Date().getMonth() + 1)
  const [tab, setTab] = useState<'commessa' | 'mensile'>('commessa')

  const { data: commesse = [] } = useQuery<Commessa[]>({
    queryKey: ['commesse-all'],
    queryFn: () => commesseApi.list().then(r => r.data),
  })

  const { data: report, isLoading: loadingReport } = useQuery<EcoReport>({
    queryKey: ['eco-report', selectedCommessa],
    queryFn: () => economiaApi.reportCommessa(selectedCommessa!).then(r => r.data),
    enabled: !!selectedCommessa,
  })

  const { data: mensile, isLoading: loadingMensile } = useQuery({
    queryKey: ['eco-mensile', anno, mese],
    queryFn: () => economiaApi.riepilogoMensile(anno, mese).then(r => r.data),
  })

  const [annoRep, setAnnoRep] = useState(new Date().getFullYear())
  const [meseRep, setMeseRep] = useState(new Date().getMonth() + 1)

  const { data: reportPersonale, isLoading: loadingRep } = useQuery({
    queryKey: ['report-personale', annoRep, meseRep],
    queryFn: () => reportPersonaleApi.get(annoRep, meseRep).then(r => r.data),
  })

  const formatEur = (v: number) =>
    `€${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp size={28} className="text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800">Report Economico</h1>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('commessa')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            tab === 'commessa' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Per Commessa
        </button>
        <button
          onClick={() => setTab('mensile')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            tab === 'mensile' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Riepilogo Mensile
        </button>
      </div>

      {tab === 'commessa' && (
        <>
          <div className="card mb-6">
            <label className="block text-sm font-medium mb-2">Seleziona Commessa</label>
            <select
              className="input w-full md:w-96"
              value={selectedCommessa || ''}
              onChange={e => setSelectedCommessa(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Scegli una commessa...</option>
              {commesse.map(c => (
                <option key={c.id} value={c.id}>{c.numero_commessa} – {c.nome}</option>
              ))}
            </select>
          </div>

          {loadingReport && <div className="text-center py-12 text-gray-500">Calcolo report...</div>}

          {report && !loadingReport && (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="card text-center">
                  <p className="text-xs text-gray-500 mb-1">Valore Contratto</p>
                  <p className="text-xl font-bold text-gray-800">{formatEur(report.commessa.contract_value)}</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500 mb-1">Costi Lavoro</p>
                  <p className="text-xl font-bold text-blue-700">{formatEur(report.costi_lavoro.total_cost)}</p>
                  <p className="text-xs text-gray-400">{report.costi_lavoro.total_hours.toFixed(1)}h totali</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500 mb-1">Spese</p>
                  <p className="text-xl font-bold text-amber-700">{formatEur(report.spese.totale)}</p>
                  <p className="text-xs text-gray-400">{report.spese.n_voci} voci</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500 mb-1">Margine</p>
                  <p className={`text-xl font-bold ${MARGIN_CLS[report.marginalita.status].text}`}>
                    {formatEur(report.marginalita.margin_amount)}
                  </p>
                  <p className={`text-xs font-semibold ${MARGIN_CLS[report.marginalita.status].text}`}>
                    {report.marginalita.margin_percentage.toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Barra marginalità */}
              <div className="card mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700">Marginalità</h3>
                  <span className={`badge ${MARGIN_CLS[report.marginalita.status].badge}`}>
                    {report.marginalita.status === 'verde' ? '✓ Buona' : report.marginalita.status === 'giallo' ? '⚠ Attenzione' : '✗ Critica'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all ${MARGIN_CLS[report.marginalita.status].bar}`}
                    style={{ width: `${Math.max(0, Math.min(100, report.marginalita.margin_percentage))}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Costi totali: {formatEur(report.marginalita.total_costs)}</span>
                  <span>Margine: {report.marginalita.margin_percentage.toFixed(1)}%</span>
                </div>
              </div>

              {/* Dettaglio costi per tecnico */}
              {report.costi_lavoro.dettaglio.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Clock size={16} /> Dettaglio Costi Lavoro
                  </h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left pb-2 text-gray-500">Tecnico</th>
                        <th className="text-right pb-2 text-gray-500">Ore</th>
                        <th className="text-right pb-2 text-gray-500">Costo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.costi_lavoro.dettaglio.map((d, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2">{d.tecnico_nome}</td>
                          <td className="text-right py-2">{d.ore.toFixed(1)}h</td>
                          <td className="text-right py-2 font-medium">{formatEur(d.costo)}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold text-gray-700">
                        <td className="pt-2">Totale</td>
                        <td className="text-right pt-2">{report.costi_lavoro.total_hours.toFixed(1)}h</td>
                        <td className="text-right pt-2">{formatEur(report.costi_lavoro.total_cost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!selectedCommessa && !loadingReport && (
            <div className="card text-center py-16 text-gray-400">
              <TrendingUp size={48} className="mx-auto mb-3 opacity-30" />
              <p>Seleziona una commessa per vedere il report economico</p>
            </div>
          )}
        </>
      )}

      {tab === 'mensile' && (
        <>
          <div className="card mb-6">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Anno</label>
                <input
                  type="number"
                  className="input w-28"
                  value={anno}
                  onChange={e => setAnno(parseInt(e.target.value))}
                  min="2020" max="2030"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mese</label>
                <select
                  className="input"
                  value={mese}
                  onChange={e => setMese(parseInt(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>
                      {new Date(2024, m - 1).toLocaleDateString('it-IT', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loadingMensile ? (
            <div className="text-center py-12 text-gray-500">Caricamento...</div>
          ) : mensile && (
            <>
              <div className="card mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">
                    Riepilogo {mensile.periodo}
                  </h3>
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertTriangle size={16} />
                    <span className="text-sm font-medium">Spese mese: {formatEur(mensile.totale_spese)}</span>
                  </div>
                </div>
              </div>

              {mensile.commesse.length === 0 ? (
                <div className="card text-center py-8 text-gray-400">Nessuna commessa attiva nel periodo</div>
              ) : (
                <div className="space-y-3">
                  {mensile.commesse.map((c: any) => (
                    <div key={c.commessa_id} className="card">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-800">{c.nome}</div>
                          <div className="text-sm text-gray-500">{c.numero_commessa}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Contratto</div>
                          <div className="font-semibold">{formatEur(c.contract_value)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Spese mese</div>
                          <div className="font-semibold text-amber-700">{formatEur(c.spese_mese)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Costi Personale per Tecnico */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px', marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>Costi Personale per Tecnico</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="select" value={meseRep} onChange={e => setMeseRep(Number(e.target.value))}>
              {['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'].map((m, i) => (
                <option key={i} value={i+1}>{m}</option>
              ))}
            </select>
            <select className="select" value={annoRep} onChange={e => setAnnoRep(Number(e.target.value))}>
              {[2024,2025,2026,2027].map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {loadingRep ? (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Caricamento…</p>
        ) : !reportPersonale || !reportPersonale.tecnici || reportPersonale.tecnici.length === 0 ? (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Nessun dato per questo periodo</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Ore lavorate', value: `${(reportPersonale.totali.ore_lavorate ?? 0).toFixed(1)} h` },
                { label: 'Costo personale', value: `€ ${(reportPersonale.totali.costo_ore ?? 0).toFixed(2)}` },
                { label: 'Costo mezzi', value: `€ ${(reportPersonale.totali.costo_mezzo ?? 0).toFixed(2)}` },
                { label: 'Trasferte', value: `€ ${(reportPersonale.totali.costo_trasferte ?? 0).toFixed(2)}` },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#64748B', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1E293B' }}>{value}</div>
                </div>
              ))}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                  {['Tecnico','Ore','Costo ore','Costo mezzo','Trasferte','Totale'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748B', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportPersonale.tecnici.map((r: any) => (
                  <tr key={r.tecnico_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '10px 10px', fontWeight: 500 }}>{r.tecnico_nome}</td>
                    <td style={{ padding: '10px 10px' }}>{r.ore_lavorate.toFixed(1)}</td>
                    <td style={{ padding: '10px 10px' }}>€ {r.costo_ore.toFixed(2)}</td>
                    <td style={{ padding: '10px 10px' }}>€ {r.costo_mezzo.toFixed(2)}</td>
                    <td style={{ padding: '10px 10px' }}>€ {r.costo_trasferte.toFixed(2)}</td>
                    <td style={{ padding: '10px 10px', fontWeight: 700, color: '#dc2626' }}>€ {r.totale.toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ background: '#F8FAFC', fontWeight: 700 }}>
                  <td style={{ padding: '10px 10px' }}>TOTALE</td>
                  <td style={{ padding: '10px 10px' }}>{(reportPersonale.totali.ore_lavorate ?? 0).toFixed(1)}</td>
                  <td style={{ padding: '10px 10px' }}>€ {(reportPersonale.totali.costo_ore ?? 0).toFixed(2)}</td>
                  <td style={{ padding: '10px 10px' }}>€ {(reportPersonale.totali.costo_mezzo ?? 0).toFixed(2)}</td>
                  <td style={{ padding: '10px 10px' }}>€ {(reportPersonale.totali.costo_trasferte ?? 0).toFixed(2)}</td>
                  <td style={{ padding: '10px 10px', color: '#dc2626' }}>€ {(reportPersonale.totali.totale ?? 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  )
}
